import React, { Component } from 'react';
import { Alert, Button, Form, Modal, Row, Col } from 'react-bootstrap';
import AceEditor from "react-ace";
import {
  SESSION_ALIAS_HTML5_PATTERN, SESSION_ALIAS_MIN_LENGTH, SESSION_ALIAS_MAX_LENGTH, validateSessionAlias
} from 'context/sessionAlias';

/**
 * CreateSessionModal — creates a new session, and (given an optional `session` prop) edits an
 * existing one. Every field is editable in both modes; the only thing that differs is the title,
 * the submit label, and what onEnter prefills.
 *
 * Submit contract: props.handleCreate({alias, description, tracing}) is called with the same shape
 * in both modes, and its return value decides what happens next:
 *   - returns nothing (undefined) — a fire-and-forget command. The existing create call sites
 *     (debugger/sessions/index.js navigates to the new session; playground/selector/index.js hands
 *     off to the session manager, which prepends it to the list) both fire the command themselves
 *     and don't hand back anything, so the modal closes immediately, as it always has.
 *   - returns a Promise — the modal awaits it and closes ONLY on `{ ok: true }`. `{ ok: false,
 *     aliasInUse: true }` surfaces a collision message under the Alias field and keeps the dialog
 *     open (update_session can 400 alias_in_use on the value just typed, same as create_session).
 *     `{ ok: false, message }` surfaces a general error above the fields and also keeps the dialog
 *     open — used for failures that aren't about the alias specifically, e.g. 409 session_not_open,
 *     which blocks every field, not just the alias.
 */
export default class CreateSessionModal extends Component {
  constructor(props) {
    super(props);
    this.form = React.createRef();
    this.state = {
      alias: "",
      aliasError: null,
      description: "",
      tracing: null,
      generalError: null,
      saving: false
    };
  }

  onEnter = () => {
    const session = this.props.session;
    if ( session ) {
      this.setState({
        alias: session.alias || "",
        aliasError: null,
        description: session.description || "",
        tracing: JSON.stringify(session.tracing || { "log_data": false }, null, 2),
        generalError: null,
        saving: false
      });
    } else {
      this.setState({
        alias: "",
        aliasError: null,
        description: "",
        tracing: JSON.stringify({
          "log_data": false
        }, null, 2),
        generalError: null,
        saving: false
      });
    }
  }

  isValidTracing() {
    try {
      JSON.parse(this.state.tracing);
      return true;
    } catch ( error ) {
      return false;
    }
  }

  handleCreate = () => {
    // Check form validity. The native constraints (pattern/minLength/maxLength on the alias field)
    // are reported first; the grammar check below runs after so the message is ours.
    if ( !this.form.current.reportValidity() ) {
      return;
    }
    // The alias is optional in both modes: a blank field is valid and is passed through as "" so the
    // caller's command-body builder omits the key entirely (which means "no change" when editing).
    const alias = this.state.alias.trim();
    if ( alias ) {
      const aliasError = validateSessionAlias(alias);
      if ( aliasError ) {
        this.setState({ aliasError: aliasError });
        return;
      }
    }
    this.setState({ aliasError: null, generalError: null });

    let tracing = null;
    try {
      tracing = JSON.parse(this.state.tracing);
    } catch ( error ) {
      return;
    }

    const session = {
      alias: alias,
      description: this.state.description,
      tracing: tracing
    };
    const result = this.props.handleCreate(session);

    // Fire-and-forget contract (see the class docblock): the caller owns the command and any
    // navigation/list update, so close right away.
    if ( !result || typeof result.then !== "function" ) {
      this.props.onHide();
      return;
    }

    // Promise contract: stay open until the outcome is known.
    this.setState({ saving: true });
    return result.then( outcome => {
      if ( outcome && outcome.ok ) {
        this.setState({ saving: false });
        this.props.onHide();
        return;
      }
      if ( outcome && outcome.aliasInUse ) {
        this.setState({
          saving: false,
          aliasError: "That alias is already in use in this revision. Pick another name.",
          generalError: null
        });
        return;
      }
      this.setState({
        saving: false,
        aliasError: null,
        generalError: ( outcome && outcome.message ) || "Failed to save the session."
      });
    });
  }

  render() {
    const isEdit = !!this.props.session;
    const title = isEdit ? "Edit Session" : "Create a New Session";
    const submitLabel = this.state.saving ? "Saving…" : ( isEdit ? "Save" : "Create" );

    return (
      <Modal
        {...this.props}
        onEnter={this.onEnter}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.generalError &&
            <Alert variant="warning">{this.state.generalError}</Alert>
          }
          <Form ref={this.form} onSubmit={(event) => {
            event.preventDefault();
            this.handleCreate();
          }}>
            <Form.Group>
              <Form.Label>
                Session is a container for shared data and tie workflow executions together into a single stream of
                coherent activity.
              </Form.Label>
            </Form.Group>
            <hr/>

            <Form.Group>
              <Form.Label>
                <strong>Alias</strong>
              </Form.Label>
              <Row>
                <Col sm={11}>
                  <Form.Control
                    type="text" size="sm"
                    value={this.state.alias}
                    onChange={(e) => this.setState({ alias: e.target.value, aliasError: null })}
                    placeholder="e.g. primary, staging, daily_report"
                    pattern={SESSION_ALIAS_HTML5_PATTERN}
                    minLength={SESSION_ALIAS_MIN_LENGTH}
                    maxLength={SESSION_ALIAS_MAX_LENGTH}
                    title={"Lower-case letters, digits, underscores and hyphens only — " +
                      "for example primary, staging or daily_report."}
                  />
                </Col>
              </Row>
              <Form.Text muted>
                Session alias must conform to the following requirements:
                <ul>
                  <li>minimum of {SESSION_ALIAS_MIN_LENGTH} characters;</li>
                  <li>maximum of {SESSION_ALIAS_MAX_LENGTH} characters;</li>
                  <li>only lower-case alphanumeric characters along with <strong>-</strong>,<strong>_</strong> are
                    {" "}allowed;</li>
                  <li>must be unique within the scope of the revision.</li>
                </ul>
              </Form.Text>
              <Form.Control.Feedback type="invalid"
                style={{"display": this.state.aliasError ? "block" : "none"}}>
                {this.state.aliasError}
              </Form.Control.Feedback>
            </Form.Group>
            <hr/>

            <Form.Group>
              <Form.Label>
                <strong>Description</strong>
              </Form.Label>
              <Row>
                <Col sm={11}>
                  <Form.Control
                    type="text" size="sm"
                    value={this.state.description}
                    onChange={(e) => this.setState({ description: e.target.value })}
                    placeholder="Enter a brief description for the session"
                  />
                </Col>
              </Row>
              <Form.Text muted>
                A short description of the session's purpose or content.
              </Form.Text>
            </Form.Group>
            <hr/>

            <Form.Group>
              <Form.Label>
                <strong>Tracing</strong>
              </Form.Label>
              <Row>
                <Col sm={11}>
                  <AceEditor
                    name="body-editor" width="100%" height="100px" mode="json" theme="monokai" wrapEnabled="true"
                    value={this.state.tracing}
                    onChange={(newValue) => {
                      this.setState({tracing: newValue});
                    }}
                  />
                </Col>
              </Row>
              <Form.Text muted>
                {/* Leftover create-mode copy used to read "Tracing properties of a new execution" --
                  * wrong on two counts in edit mode: the session being edited already exists (it's
                  * not "new"), and this tracing belongs to the session, not an execution. */}
                {isEdit ? "Tracing properties of this session." : "Tracing properties of the new session."}
              </Form.Text>
              <Form.Control.Feedback type="invalid" style={{"display": this.isValidTracing() ? "none" : "block"}}>
                Invalid JSON provided
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.props.onHide}>Close</Button>
          <Button variant="success" disabled={this.state.saving} onClick={this.handleCreate}>
            {submitLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
